import { delay } from "@/lib/utils";
import { mockIds } from "../mockIds";
import { LevelTestSession, LevelTestSessionStatus, LevelTestAnswer } from "./studentLevelTest.interfaces";

// Mock data persistente - mantém estado entre chamadas
const mockSessions: Map<string, LevelTestSession | null> = new Map();

// Helper para criar respostas com informações completas do exercício
const createAnswerWithExercise = (
  exerciseId: string,
  answerData: string,
  isCorrect: boolean,
  answeredAt: string,
  exerciseData: {
    title: string;
    description?: string;
    type: string;
    data: any;
    moduleId: string;
    moduleName: string;
  }
): LevelTestAnswer => ({
  exerciseId,
  answerData,
  isCorrect,
  answeredAt,
  exercise: {
    id: exerciseId,
    title: exerciseData.title,
    description: exerciseData.description,
    type: exerciseData.type,
    data: exerciseData.data,
    moduleId: exerciseData.moduleId,
    module: {
      id: exerciseData.moduleId,
      name: exerciseData.moduleName,
      title: exerciseData.moduleName,
    },
  },
});

// Inicializar alguns dados mockados para desenvolvimento
const initializeMockData = () => {
  // Estudante 1 tem sessão completada
  mockSessions.set(mockIds.user1, {
    id: "session-001",
    status: "Completed",
    startedAt: "2024-01-15T10:00:00Z",
    completedAt: "2024-01-15T10:30:00Z",
    finalLevel: 3,
    finalLevelName: "Intermediate",
    score: 85.5,
    answers: [
      createAnswerWithExercise(
        "ex-001",
        JSON.stringify([2]), // Array de inteiros (order da opção selecionada)
        true,
        "2024-01-15T10:05:00Z",
        {
          title: "Choose the correct verb form",
          description: "Select the appropriate verb form for the sentence",
          type: "multiple_choice",
          data: {
            question: "I ___ to the store yesterday.",
            options: [
              { text: "go", order: 1, isCorrect: false },
              { text: "went", order: 2, isCorrect: true },
              { text: "gone", order: 3, isCorrect: false },
              { text: "going", order: 4, isCorrect: false },
            ],
            multipleCorrectAnswers: false,
          },
          moduleId: mockIds.module1,
          moduleName: "Past Tense Basics",
        }
      ),
      createAnswerWithExercise(
        "ex-002",
        JSON.stringify([3]), // Array de inteiros (order da opção selecionada)
        false,
        "2024-01-15T10:10:00Z",
        {
          title: "Vocabulary: Food items",
          description: "Identify the correct word",
          type: "multiple_choice",
          data: {
            question: "Which word means 'a place where you buy food'?",
            options: [
              { text: "restaurant", order: 1, isCorrect: false },
              { text: "kitchen", order: 2, isCorrect: false },
              { text: "grocery store", order: 3, isCorrect: true },
              { text: "bakery", order: 4, isCorrect: false },
            ],
            multipleCorrectAnswers: false,
          },
          moduleId: mockIds.module2,
          moduleName: "Food and Dining",
        }
      ),
      createAnswerWithExercise(
        "ex-003",
        JSON.stringify([2]), // Array de inteiros (order da opção selecionada)
        true,
        "2024-01-15T10:15:00Z",
        {
          title: "Reading comprehension",
          description: "Answer the question based on the text",
          type: "multiple_choice",
          data: {
            question: "What is the main idea of the passage?",
            options: [
              { text: "Option A", order: 1, isCorrect: false },
              { text: "Option B (Correct)", order: 2, isCorrect: true },
              { text: "Option C", order: 3, isCorrect: false },
              { text: "Option D", order: 4, isCorrect: false },
            ],
            multipleCorrectAnswers: false,
          },
          moduleId: mockIds.module3,
          moduleName: "Reading Skills",
        }
      ),
      createAnswerWithExercise(
        "ex-004",
        JSON.stringify([1]), // Array de inteiros (order da opção selecionada)
        true,
        "2024-01-15T10:20:00Z",
        {
          title: "Grammar: Present continuous",
          description: "Choose the correct form",
          type: "multiple_choice",
          data: {
            question: "She ___ studying right now.",
            options: [
              { text: "is", order: 1, isCorrect: true },
              { text: "are", order: 2, isCorrect: false },
              { text: "am", order: 3, isCorrect: false },
              { text: "be", order: 4, isCorrect: false },
            ],
            multipleCorrectAnswers: false,
          },
          moduleId: mockIds.module1,
          moduleName: "Present Tense Basics",
        }
      ),
      createAnswerWithExercise(
        "ex-005",
        JSON.stringify([4]), // Array de inteiros (order da opção selecionada)
        true,
        "2024-01-15T10:25:00Z",
        {
          title: "Vocabulary: Travel",
          description: "Select the correct word",
          type: "multiple_choice",
          data: {
            question: "What do you call a person who travels?",
            options: [
              { text: "travel", order: 1, isCorrect: false },
              { text: "trip", order: 2, isCorrect: false },
              { text: "journey", order: 3, isCorrect: false },
              { text: "traveler", order: 4, isCorrect: true },
            ],
            multipleCorrectAnswers: false,
          },
          moduleId: mockIds.module2,
          moduleName: "Travel and Transportation",
        }
      ),
    ],
    correctAnswers: 4,
    scorePercentage: 80.0,
    skillBreakdown: {
      Grammar: 85.0,
      Vocabulary: 75.0,
      Comprehension: 80.0,
    },
  });

  // Estudante 2 tem sessão ativa
  mockSessions.set(mockIds.user2, {
    id: "session-002",
    status: "Active",
    startedAt: "2024-01-20T14:00:00Z",
    completedAt: undefined,
    finalLevel: undefined,
    finalLevelName: undefined,
    score: undefined,
    answers: [
      createAnswerWithExercise(
        "ex-006",
        JSON.stringify([1]), // Array de inteiros (order da opção selecionada)
        true,
        "2024-01-20T14:05:00Z",
        {
          title: "Basic grammar question",
          type: "multiple_choice",
          data: {
            question: "What is the correct answer?",
            options: [
              { text: "Correct answer", order: 1, isCorrect: true },
              { text: "Wrong answer", order: 2, isCorrect: false },
            ],
            multipleCorrectAnswers: false,
          },
          moduleId: mockIds.module1,
          moduleName: "Grammar Basics",
        }
      ),
      createAnswerWithExercise(
        "ex-007",
        JSON.stringify([2]), // Array de inteiros (order da opção selecionada)
        true,
        "2024-01-20T14:10:00Z",
        {
          title: "Vocabulary question",
          type: "multiple_choice",
          data: {
            question: "Choose the right word",
            options: [
              { text: "Wrong", order: 1, isCorrect: false },
              { text: "Correct", order: 2, isCorrect: true },
            ],
            multipleCorrectAnswers: false,
          },
          moduleId: mockIds.module2,
          moduleName: "Vocabulary Building",
        }
      ),
    ],
  });

  // Estudante 3 tem sessão abortada
  mockSessions.set(mockIds.user3, {
    id: "session-003",
    status: "Aborted",
    startedAt: "2024-01-18T09:00:00Z",
    completedAt: "2024-01-18T09:15:00Z",
    finalLevel: undefined,
    finalLevelName: undefined,
    score: undefined,
    answers: [
      createAnswerWithExercise(
        "ex-008",
        JSON.stringify([3]), // Array de inteiros (order da opção selecionada)
        false,
        "2024-01-18T09:05:00Z",
        {
          title: "Incomplete question",
          type: "multiple_choice",
          data: {
            question: "This question was not completed",
            options: [
              { text: "Option 1", order: 1, isCorrect: false },
              { text: "Option 2", order: 2, isCorrect: false },
              { text: "Option 3 (Selected)", order: 3, isCorrect: false },
              { text: "Option 4 (Correct)", order: 4, isCorrect: true },
            ],
            multipleCorrectAnswers: false,
          },
          moduleId: mockIds.module3,
          moduleName: "Intermediate Grammar",
        }
      ),
    ],
  });

  // Estudantes 4 e 5 não têm sessão (null)
  mockSessions.set(mockIds.user4, null);
  mockSessions.set(mockIds.user5, null);
};

// Inicializar dados mockados
initializeMockData();

/**
 * Busca a sessão de teste de nivelamento atual de um estudante
 * @param studentId ID do estudante
 * @returns Sessão de teste ou null se não houver sessão
 */
export const getStudentLevelTestSession = async (
  studentId: string
): Promise<LevelTestSession | null> => {
  await delay(100);
  return mockSessions.get(studentId) || null;
};

/**
 * Remove a sessão de teste de nivelamento de um estudante
 * @param studentId ID do estudante
 */
export const deleteStudentLevelTestSession = async (
  studentId: string
): Promise<void> => {
  await delay(100);
  mockSessions.delete(studentId);
};

/**
 * Busca detalhes completos de uma sessão de teste
 * @param sessionId ID da sessão
 * @returns Detalhes completos da sessão
 */
export const getLevelTestSessionDetails = async (
  sessionId: string
): Promise<LevelTestSession> => {
  await delay(100);
  
  // Buscar sessão pelo ID
  for (const session of mockSessions.values()) {
    if (session && session.id === sessionId) {
      return session;
    }
  }
  
  throw new Error(`Level test session with ID ${sessionId} not found`);
};